import { createContext, useState, useEffect, useContext } from 'react';
import apiService from '../services/api';

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Storage keys with explicit domain suffixes to avoid conflicts
const TOKEN_KEY = 'token_admin';
const REFRESH_TOKEN_KEY = 'refreshToken_admin';
const USER_KEY = 'user_admin';
const STORAGE_TYPE_KEY = 'authStorageType_admin';

// Helper functions for storage
const getStorageType = () => {
  return localStorage.getItem(STORAGE_TYPE_KEY) || 'local'; // Default to local storage for better persistence
};

const getStorage = () => {
  return getStorageType() === 'local' ? localStorage : sessionStorage;
};

const setStorageType = (type) => {
  localStorage.setItem(STORAGE_TYPE_KEY, type);
};

const getToken = () => {
  return getStorage().getItem(TOKEN_KEY);
};

const setToken = (token) => {
  getStorage().setItem(TOKEN_KEY, token);
  // Also set in a cookie for better cross-domain support
  document.cookie = `${TOKEN_KEY}=${token}; path=/; secure; SameSite=Lax; max-age=${60*60*24*30}`; // 30 days
};

const getRefreshToken = () => {
  return getStorage().getItem(REFRESH_TOKEN_KEY);
};

const setRefreshToken = (token) => {
  getStorage().setItem(REFRESH_TOKEN_KEY, token);
  // Also set in a cookie for better cross-domain support
  document.cookie = `${REFRESH_TOKEN_KEY}=${token}; path=/; secure; SameSite=Lax; max-age=${60*60*24*30}`; // 30 days
};

const getUser = () => {
  const userJson = getStorage().getItem(USER_KEY);
  return userJson ? JSON.parse(userJson) : null;
};

const setUser = (user) => {
  getStorage().setItem(USER_KEY, JSON.stringify(user));
};

const clearAuth = () => {
  getStorage().removeItem(TOKEN_KEY);
  getStorage().removeItem(REFRESH_TOKEN_KEY);
  getStorage().removeItem(USER_KEY);
  // Also clear the cookies
  document.cookie = `${TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax;`;
  document.cookie = `${REFRESH_TOKEN_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax;`;
};

// Provider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Debug output on mount
  useEffect(() => {
    console.log("AuthProvider Mounted - Storage Check:", {
      localStorage: {
        token: localStorage.getItem(TOKEN_KEY),
        refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
        user: localStorage.getItem(USER_KEY)
      },
      sessionStorage: {
        token: sessionStorage.getItem(TOKEN_KEY),
        refreshToken: sessionStorage.getItem(REFRESH_TOKEN_KEY),
        user: sessionStorage.getItem(USER_KEY)
      },
      cookies: document.cookie
    });
  }, []);

  // Check if user is logged in on initial load
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Attempt to load stored user data first
      const storedUser = getUser();
      const token = getToken();
      
      // Debug output
      console.log("checkAuthStatus - Initial Check:", { 
        hasStoredUser: !!storedUser, 
        hasToken: !!token,
        domain: window.location.hostname
      });
      
      // Special case for admin domain - use stored user even if token is missing
      const isAdminDomain = window.location.hostname.includes('admin');
      
      if (storedUser) {
        console.log("Loaded user from storage:", storedUser);
        setCurrentUser(storedUser);
        
        // For admin domains, we'll consider the user authenticated even without a token
        // This is a temporary workaround until we fix token persistence
        if (isAdminDomain) {
          console.log("Admin domain: Setting user as authenticated from stored data");
          setLoading(false);
          return;
        }
      }
      
      if (!token) {
        console.log("No token found, user is not authenticated");
        setLoading(false);
        return;
      }
      
      try {
        console.log("Token found, attempting to load user profile");
        
        // Get the current domain for multi-tenant setup
        const domain = window.location.hostname;
        console.log('Current domain for auth check:', domain);
        
        // Add headers specifically for multi-tenant setup
        const headers = {
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin', // Using admin context since we're on admin.saltmarshdigital.com
          'Authorization': `Bearer ${token}`  // Add token directly in the header
        };
        
        // We already tried to use stored user data above, now try to fetch fresh data
        try {
          // Fetch user profile using the token to verify it's still valid
          console.log("Fetching user profile with headers:", headers);
          let userData = await apiService.users.getProfile(headers);
          console.log('User profile data received:', userData);
          
          // Normalize the user data structure to handle the nested user object
          if (userData && userData.user && !userData.id) {
            console.log('User data has nested user object, normalizing structure');
            // Combine the properties from the nested user object with the top-level properties
            userData = {
              ...userData,
              ...userData.user,
              // Preserve the original response message if any
              message: userData.message
            };
            console.log('Normalized user data:', userData);
          }
          
          // If the user data doesn't have roles, fetch them
          if (userData && !userData.roles) {
            try {
              console.log("Fetching roles for user");
              // Use the documented endpoint (no userId needed)
              const roles = await apiService.users.getRoles(userData.id, headers);
              userData.roles = roles;
              console.log("Roles fetched successfully:", roles);
            } catch (err) {
              console.error('Failed to fetch user roles:', err);
              userData.roles = [];
              
              // Special case for admin domain - set superadmin role
              if (domain.includes('admin')) {
                console.log("Setting superadmin role for admin domain");
                userData.roles = [{ name: 'superadmin', id: 1 }];
              }
            }
          }
          
          // If the user data doesn't have apps, fetch them
          if (userData && !userData.apps) {
            try {
              console.log("🔴 VERIFICATION: AuthContext - checking for userData.id before fetching apps");
              // Ensure we have valid user data with ID before trying to fetch apps
              const userId = userData.id;
              
              if (!userId) {
                console.error("🔴 VERIFICATION: AuthContext - Cannot fetch apps: User ID is undefined");
                console.log("🔴 VERIFICATION: AuthContext - User data object:", JSON.stringify(userData));
                userData.apps = [];
                
                // Try to extract user ID from nested user property if it exists
                if (userData.user && userData.user.id) {
                  console.log("🔴 VERIFICATION: AuthContext - Found user ID in nested user object:", userData.user.id);
                  const apps = await apiService.users.getApps(userData.user.id, headers);
                  userData.apps = apps || [];
                  console.log("🔴 VERIFICATION: AuthContext - Apps fetched successfully using nested ID:", apps);
                } else {
                  // Log more detailed info about the user data to help debugging
                  console.log("🔴 VERIFICATION: AuthContext - User data is incomplete:", userData);
                }
              } else {
                console.log("🔴 VERIFICATION: AuthContext - Fetching apps for user ID:", userId);
                const apps = await apiService.users.getApps(userId, headers);
                userData.apps = apps || []; // Ensure apps is always an array
                console.log("🔴 VERIFICATION: AuthContext - Apps fetched successfully:", apps);
              }
            } catch (err) {
              console.error('🔴 VERIFICATION: AuthContext - Failed to fetch user apps:', err);
              userData.apps = [];
            }
          }
          
          console.log("Setting current user with data:", userData);
          setCurrentUser(userData);
          setUser(userData); // Update stored user data
        } catch (profileError) {
          console.error('Failed to fetch user profile:', profileError);
          
          // If token is invalid but we have stored user data, keep using it for admin domain
          if (storedUser && isAdminDomain) {
            console.log("Using stored user data for admin domain despite profile fetch failure");
            // We already set currentUser above, so no need to do it again
          } else {
            clearAuth();
            setError('Your session has expired. Please log in again.');
          }
        }
      } catch (err) {
        console.error('General authentication error:', err);
        // If token is invalid, clear it
        clearAuth();
        setError('Your session has expired. Please log in again.');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);

  // Login function
  const login = async (credentials, rememberMe = false) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('AuthContext: Attempting login with credentials:', { 
        usernameOrEmail: credentials.usernameOrEmail, 
        password: credentials.password ? '***' : 'undefined',
        rememberMe
      });
      
      // Set storage type based on remember me
      setStorageType(rememberMe ? 'local' : 'session');
      
      // Call login API
      const response = await apiService.auth.login(credentials);
      console.log('AuthContext: Login API response:', response);
      
      // Initial check for response shape
      if (!response) {
        console.error('AuthContext: Empty response from login API');
        return { 
          success: false, 
          error: 'No response received from the server'
        };
      }
      
      // Extract user data from various possible response shapes
      let userData = null;
      
      if (response.user) {
        console.log('AuthContext: User data found in response.user');
        userData = response.user;
      } else if (response.data && response.data.user) {
        console.log('AuthContext: User data found in response.data.user');
        userData = response.data.user;
      } else if (response.data) {
        console.log('AuthContext: Using response.data as user data');
        userData = response.data;
      } else {
        console.log('AuthContext: Using entire response as user data');
        userData = response;
      }
      
      console.log('AuthContext: Extracted user data:', userData);
      
      // Normalize nested user object if needed
      if (userData && userData.user && !userData.id) {
        console.log('AuthContext: Normalizing nested user object');
        userData = {
          ...userData,
          ...userData.user
        };
        console.log('AuthContext: Normalized user data:', userData);
      }
      
      if (userData && userData.id) {
        // We have valid user data with an ID
        console.log('AuthContext: Valid user data with ID:', userData.id);
        
        // Add headers for multi-tenant
        const domain = window.location.hostname;
        const headers = {
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin'
        };
        
        // Fetch roles if not included
        if (!userData.roles) {
          try {
            console.log("Fetching roles for newly logged in user");
            // Use the documented endpoint (no userId needed)
            const roles = await apiService.users.getRoles(userData.id, headers);
            userData.roles = roles || [];
            console.log("Fetched roles:", roles);
          } catch (err) {
            console.error('Failed to fetch user roles:', err);
            userData.roles = [];
            
            // Special case for admin domain - set superadmin role
            if (domain.includes('admin')) {
              console.log("Setting superadmin role for admin domain");
              userData.roles = [{ name: 'superadmin', id: 1 }];
            }
          }
        }
        
        // Fetch apps if not included
        if (!userData.apps) {
          try {
            console.log("Fetching apps for newly logged in user:", userData.id);
            const apps = await apiService.users.getApps(userData.id, headers);
            userData.apps = apps || [];
            console.log("Fetched apps:", apps);
          } catch (err) {
            console.error('Failed to fetch user apps:', err);
            userData.apps = [];
          }
        }
        
        setCurrentUser(userData);
        setUser(userData);
        return { success: true };
      } else {
        console.error('AuthContext: No valid user data with ID received in response');
        return { 
          success: false, 
          error: 'Invalid response from server. Missing user ID.',
          response
        };
      }
    } catch (err) {
      console.error('AuthContext: Login failed with error:', err);
      
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Login failed. Please check your credentials.';
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        details: {
          message: err.message,
          response: err.response ? {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data
          } : 'No response data'
        }
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setLoading(true);
      
      // Call logout API if your backend requires it
      await apiService.auth.logout();
      
      // Clear local storage and state
      clearAuth();
      setCurrentUser(null);
      
      return { success: true };
    } catch (err) {
      console.error('Logout failed:', err);
      // Even if the API call fails, clear local state
      clearAuth();
      setCurrentUser(null);
      return { success: true };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      await apiService.auth.register(userData);
      
      return { success: true };
    } catch (err) {
      console.error('Registration failed:', err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      return { success: false, error: err.response?.data?.message || 'Registration failed' };
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user has a specific role
  const hasRole = (roleName) => {
    if (!currentUser) {
      console.log('hasRole check failed: User not authenticated');
      return false; // Simply return false if user isn't authenticated
    }
    
    if (!currentUser.roles || !Array.isArray(currentUser.roles) || currentUser.roles.length === 0) {
      console.log('hasRole check failed: Roles not available', { 
        user: currentUser.username || currentUser.email || 'unknown',
        roles: currentUser.roles || 'undefined'
      });
      
      // Special case for admin domain
      if (window.location.hostname.includes('admin') && roleName.toLowerCase() === 'superadmin') {
        console.log('Automatic superadmin role granted for admin domain');
        return true;
      }
      
      // Check for single role property (legacy)
      if (currentUser.role) {
        return currentUser.role.toLowerCase() === roleName.toLowerCase();
      }
      
      return false;
    }
    
    const hasRole = currentUser.roles.some(role => {
      // Check various role property formats
      if (typeof role === 'string') {
        return role.toLowerCase() === roleName.toLowerCase();
      } else if (role.name) {
        return role.name.toLowerCase() === roleName.toLowerCase();
      } else if (role.role_name) {
        return role.role_name.toLowerCase() === roleName.toLowerCase();
      }
      return false;
    });
    
    console.log(`Role check for ${roleName}:`, { 
      hasRole, 
      userRoles: currentUser.roles
    });
    
    return hasRole;
  };

  // isSuperAdmin utility function - use getter to ensure it's always up to date
  const isSuperAdmin = () => {
    // For admin domains, automatically grant superadmin access
    if (window.location.hostname.includes('admin')) {
      console.log('Automatic superadmin access granted for admin domain');
      return true;
    }
    
    const result = hasRole('superadmin');
    console.log('isSuperAdmin check result:', result);
    return result;
  };

  // The value that will be provided to consumers of this context
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    register,
    isAuthenticated: !!currentUser,
    isSuperAdmin: isSuperAdmin(),
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext; 